import * as React from 'react';
import moment from 'moment';
import {AppUser} from 'Src/views/App/App';
import {SchoolEvent, MatchResult} from 'Src/models/event';
import {getMatchFixtures, saveMatchFixtures, matchResultPayload} from 'Src/helpers/service/admin/eventResults';
import {RichMatchResultEditor} from 'Src/components/RichMatchResultEditor/RichMatchResultEditor';
import {Button} from 'Src/components/Button/Button';
import {SimpleModal} from 'Src/components/SimpleModal/SimpleModal';
import {getSchoolEvent} from 'Src/helpers/service/admin/event';
import {EventMatchLabel} from 'Src/components/EventMatchLabel/EventMatchLabel';
import './MultiTeamMatchResults.scss';

interface Side {key: string; type: 'TEAM' | 'SCHOOL'; schoolId: string; name: string;}
interface Row {
  id: string; home: string; away: string; state: 'PENDING' | 'NOT_PLAYED' | 'PLAYED'; label: string;
  eventId?: string; match?: {id: string; matchResult: MatchResult; status: string; revision?: number};
  matchResult?: MatchResult; startTime?: string; endTime?: string;
}
interface Fixtures {revision: number; snapshot: string; canManage: boolean; finished: boolean; sides: Side[]; rows: Row[];}
interface Props {user: AppUser; event: SchoolEvent; onSaved: () => void; onDirty: (dirty: boolean) => void;}
const newId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(12))).map(value => (`0${value.toString(16)}`).slice(-2)).join('');

export const MultiTeamMatchResults: React.FC<Props> = ({user, event, onSaved, onDirty}) => {
  const [data, setData] = React.useState<Fixtures>();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [confirmation, setConfirmation] = React.useState<'SAVE' | 'FINISH'>();
  const [dirty, setDirty] = React.useState(false);
  const [addingMeeting, setAddingMeeting] = React.useState(false);
  const [meetingPair, setMeetingPair] = React.useState('');
  const [editingChild, setEditingChild] = React.useState<{rowId: string; event: SchoolEvent}>();
  const [labelEditing, setLabelEditing] = React.useState(false);
  const childSaving = React.useRef(false);
  const openChild = async (row: Row) => {
    setBusy(true); setError('');
    try {
      const child = await getSchoolEvent(user, row.eventId, {fresh: true});
      if (child.status !== 'FINISHED' || !child.sourceEventId || String(child.sourceEventId) !== String(event.id) ||
        child.sourceMatchRowId !== row.id) {
        throw new Error('This match is unavailable. Refresh the match list before editing.');
      }
      setEditingChild({rowId: row.id, event: child});
    } catch (e) {setError(message(e));}
    finally {setBusy(false);}
  };
  const saveInFlight = React.useRef(false);
  React.useEffect(() => {
    if (!confirmation || typeof document === 'undefined') return;
    const previous = document.activeElement as HTMLElement;
    const dialog = document.querySelector<HTMLElement>('.multiTeamMatchConfirmation');
    dialog?.querySelector<HTMLButtonElement>('.btn-primary')?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation();
        if (!saveInFlight.current) setConfirmation(undefined);
      }
    };
    dialog?.addEventListener('keydown', closeOnEscape);
    return () => {
      dialog?.removeEventListener('keydown', closeOnEscape);
      if (previous?.isConnected) previous.focus();
    };
  }, [confirmation]);
  const message = (e: any) => e?.response?.data?.details?.text || e?.response?.data?.message || e?.message || 'Unable to save matches';
  const load = () => {
    setBusy(true); setError('');
    getMatchFixtures(user, event.id).then(value => {setData(value); setDirty(false); onDirty(false);})
      .catch(e => setError(message(e))).finally(() => setBusy(false));
  };
  React.useEffect(load, [event.id]);
  const update = (id: string, patch: Partial<Row>, markDirty = true) => {
    setData(current => ({...current, rows: current.rows.map(row => row.id === id ? {...row, ...patch} : row)}));
    setConfirmation(undefined);
    if (markDirty) {setDirty(true); onDirty(true);}
  };
  const sideLabel = (key: string) => {
    const side = data.sides.find(item => item.key === key);
    const school = [event.inviterSchool, ...(event.invitedSchools || [])].find(item => String(item?.id || (item as any)?._id) === side?.schoolId);
    return side?.type === 'SCHOOL' ? school?.name || side.name : side?.schoolId === String(user.activeSchoolId) ? side.name : [school?.name, side?.name].filter(Boolean).join(' · ');
  };
  const addMeeting = (row: Row) => {
    setData(current => ({...current, rows: [...current.rows, {id: newId(), home: row.home, away: row.away, state: 'PENDING', label: ''}]}));
    setDirty(true); onDirty(true); setConfirmation(undefined);
    setAddingMeeting(false);
  };
  const virtualEvent = (row: Row): SchoolEvent => {
    const keys = [row.home, row.away], teams = (event.embeddedTeams || []).filter(team => keys.includes(String((team as any).id || team._id)));
    return {...event, id: row.id, sourceEventId: event.id, tournament: undefined, multiparty: false,
      matchResult: row.matchResult, resultPublication: undefined,
      embeddedTeams: teams,
      sourceMatchSides: keys.map(key => ({sourceId: key, competitorId: key, type: data.sides.find(side => side.key === key).type})),
      teamsData: (event.teamsData || []).filter(team => keys.includes(String(team.id || (team as any)._id))),
      players: (event.players || []).filter(player => keys.includes(String(player.teamId)))
    };
  };
  const save = async (finish: boolean) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    let didSave = false;
    setBusy(true); setError('');
    try {
      const rows = data.rows.map(row => ({id: row.id, home: row.home, away: row.away, state: row.state, label: row.label,
        ...(!row.eventId && row.state === 'PLAYED' ? {
          matchResult: row.matchResult && matchResultPayload(row.matchResult),
          startTime: row.startTime || undefined, endTime: row.endTime || undefined
        } : {})}));
      const saved = await saveMatchFixtures(user, event.id, {revision: data.revision, snapshot: data.snapshot, rows, finish});
      setData(saved); setDirty(false); onDirty(false); setConfirmation(undefined); didSave = true;
    } catch (e) {setError(message(e)); setConfirmation(undefined);}
    finally {saveInFlight.current = false; setBusy(false);}
    if (didSave) onSaved();
  };
  const newMatches = data?.rows.filter(row => row.state === 'PLAYED' && !row.eventId).length || 0;
  const isReviewed = (row: Row) => {
    if (row.state === 'NOT_PLAYED') return true;
    if (row.state !== 'PLAYED') return false;
    if (row.eventId) return row.match?.status === 'FINISHED' && Boolean(row.match.matchResult?.regulation);
    const result = row.matchResult;
    if (!result?.regulation) return false;
    const scores = [result.regulation, result.halfTime, result.extraTime, result.shootout].filter(Boolean);
    if (!scores.every(score => [score.home, score.away].every(value => Number.isFinite(value) && value >= 0))) return false;
    if (result.shootout && result.shootout.home === result.shootout.away) return false;
    if ((result.scorers || []).some(scorer => !scorer.playerNameSnapshot?.trim())) return false;
    const start = new Date(row.startTime || event.startTime).getTime(), end = new Date(row.endTime || event.endTime).getTime();
    return Number.isFinite(start) && Number.isFinite(end) && end > start;
  };
  const reviewed = data?.rows.filter(isReviewed).length || 0;
  const resolved = Boolean(data?.rows.length && reviewed === data.rows.length);
  const pairs = data?.rows.filter((row, index, all) => all.findIndex(item => [item.home, item.away].sort().join(':') === [row.home, row.away].sort().join(':')) === index) || [];

  return <section className="multiTeamMatches" aria-busy={busy}>
    <p>Record the matches played at this competition. Each saved match becomes a finished event visible to both schools. Overall points or places can be recorded separately.</p>
    <p className="multiTeamMatches__note">Squads and attendance are copied from this competition. Review each created match if its squad or attendance differed. No creation or team-addition notifications are sent.</p>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {!data && !busy && <Button text="Retry" onClick={load} />}
    {data && <>
      {!data.canManage && <p>Only the organising school can create matches. Open a match to view or correct its result.</p>}
      <div className="multiTeamMatches__toolbar">
        <span role="status">{reviewed} of {data.rows.length} matches reviewed{data.finished ? ' · Competition finished' : ''}</span>
        <div className="multiTeamMatches__tools">
          {!dirty && <button type="button" className="multiTeamMatches__textAction" disabled={busy} onClick={load}>↻ Refresh results</button>}
          {data.canManage && data.rows.length < 200 && <Button customClass="btn-secondary" text="Add match" disabled={busy || addingMeeting}
            onClick={() => {setMeetingPair(pairs[0]?.id || ''); setAddingMeeting(true); setConfirmation(undefined);}} />}
        </div>
      </div>
      {addingMeeting && <div className="multiTeamMatches__addMeeting">
        <label htmlFor="multiTeamMatchPair">Teams</label>
        <select id="multiTeamMatchPair" className="form-control" value={meetingPair} disabled={busy} onChange={e => setMeetingPair(e.target.value)}>
          {pairs.map(row => <option key={row.id} value={row.id}>{sideLabel(row.home)} vs {sideLabel(row.away)}</option>)}
        </select>
        <Button text="Add meeting" customClass="btn-secondary" disabled={busy || !meetingPair} onClick={() => addMeeting(pairs.find(row => row.id === meetingPair))} />
        <button type="button" className="multiTeamMatches__textAction" disabled={busy} onClick={() => setAddingMeeting(false)}>Cancel</button>
      </div>}
      <fieldset disabled={busy}>
        {data.rows.map((row, index) => <article className="multiTeamMatches__row" key={row.id}>
          <header><strong>{sideLabel(row.home)} <span>v</span> {sideLabel(row.away)}</strong><small>Match {index + 1}</small></header>
          {row.eventId ? <div className="multiTeamMatches__saved">
            {row.match?.matchResult?.regulation ? <><div className="multiTeamMatches__savedScore"><strong>{row.match.matchResult.regulation.home}–{row.match.matchResult.regulation.away}</strong>
              {row.match.matchResult.shootout && <span>Penalties {row.match.matchResult.shootout.home}–{row.match.matchResult.shootout.away}</span>}
              {row.label && <span className="multiTeamMatches__stage">{row.label}</span>}</div>
              <div className="multiTeamMatches__tools">
                <button type="button" className="multiTeamMatches__textAction" disabled={busy} onClick={() => openChild(row)}>Edit result</button>
                <a href={`/events/event?id=${encodeURIComponent(row.eventId)}`} target="_blank" rel="noopener noreferrer">Open match ↗</a>
              </div>
            </> : <span>Match is unavailable. Restore it before continuing.</span>}
          </div> : data.canManage ? <>
            <div className="multiTeamMatches__states" role="group" aria-label={`Match ${index + 1} status`}>
              {(['PENDING', 'NOT_PLAYED', 'PLAYED'] as const).map(state => <Button key={state}
                customClass={row.state === state ? 'btn-primary' : 'btn-secondary'}
                text={state === 'PENDING' ? 'Not entered' : state === 'NOT_PLAYED' ? 'Not played' : 'Played'}
                onClick={() => update(row.id, {state})} />)}
            </div>
            <label>Stage / match label <input className="form-control" maxLength={100} value={row.label} placeholder="Optional, e.g. Final" onChange={e => update(row.id, {label: e.target.value})} /></label>
            {row.state === 'PLAYED' && <>
              <div className="multiTeamMatches__times">{(['startTime', 'endTime'] as const).map(field => <label key={field}>
                {field === 'startTime' ? 'Start' : 'End'}
                <input className="form-control" type="datetime-local" value={row[field] || event[field] ? moment(row[field] || event[field]).format('YYYY-MM-DDTHH:mm') : ''}
                  onChange={e => update(row.id, {[field]: e.target.value ? moment(e.target.value).toISOString() : undefined})} />
              </label>)}</div>
              <RichMatchResultEditor user={user} event={virtualEvent(row)} onSaved={() => {}}
                onDraftChange={result => update(row.id, {matchResult: result}, false)} />
            </>}
          </> : <p>{row.state === 'NOT_PLAYED' ? 'Not played' : 'Not entered'}</p>}
          {row.eventId && data.canManage && <label>Stage / match label <input className="form-control" maxLength={100} value={row.label}
            placeholder="Optional, e.g. Final" onChange={e => update(row.id, {label: e.target.value})} /></label>}
        </article>)}
      </fieldset>
      {data.canManage && (dirty || !data.finished) && <div className="multiTeamMatches__footer">
        <div className="multiTeamMatches__actions">
          <span>
            {dirty && 'Unsaved changes. '}
            {resolved ? 'All matches reviewed' : `Review ${data.rows.length - reviewed} remaining ${data.rows.length - reviewed === 1 ? 'match' : 'matches'} to finish competition`}
          </span>
          {dirty && <Button text={busy ? 'Please wait…' : 'Save matches'} disabled={busy || (data.finished && !resolved)} onClick={() => setConfirmation('SAVE')} />}
          {!data.finished && resolved && <Button text="Finish competition" customClass={dirty ? 'btn-secondary' : 'btn-primary'} disabled={busy} onClick={() => setConfirmation('FINISH')} />}
        </div>
      </div>}
      <SimpleModal isOpen={Boolean(editingChild)} title="Edit match result" customClass="modal-lg eResultsModal multiTeamChildEditor"
        zIndex={1060} showFooter={false} isBodyBold={false} guardUnsavedChanges={!labelEditing}
        onCloseClick={() => {if (!labelEditing && !childSaving.current) setEditingChild(undefined);}}>
        {editingChild && <div className="eResultsModal__content">
          <EventMatchLabel event={editingChild.event} user={user} onEditingChange={setLabelEditing} onSaved={saved => {
            setData(current => ({...current, revision: saved.revision, snapshot: saved.snapshot,
              rows: current.rows.map(row => row.id === editingChild.rowId ? {...row, label: saved.label} : row)}));
            setEditingChild(current => ({...current, event: {...current.event, sourceMatchLabel: saved.label}}));
          }} />
          {labelEditing && <p>Save or cancel the label before saving the result.</p>}
          <RichMatchResultEditor user={user} event={editingChild.event} saveDisabled={labelEditing}
            onSavingChange={saving => {childSaving.current = saving;}} onSaved={(published, saved) => {
            update(editingChild.rowId, {match: {id: editingChild.event.id, status: saved?.status || 'FINISHED',
              matchResult: saved.matchResult, revision: saved.matchResultRevision}}, false);
            setEditingChild(undefined);
          }} />
        </div>}
      </SimpleModal>
      <SimpleModal isOpen={Boolean(confirmation)} title={confirmation === 'FINISH' ? 'Finish competition?' : 'Save match results?'}
        customClass="multiTeamMatchConfirmation" dialogStyle={{maxWidth: 440}} zIndex={1060} isBodyBold={false}
        buttonText={busy ? 'Saving…' : confirmation === 'FINISH' ? 'Finish competition' : 'Save matches'}
        buttonCancelText="Back to results" buttonDisabled={busy} hideCancelButton={busy}
        onButtonClick={() => save(confirmation === 'FINISH')}
        onCloseClick={() => {if (!busy) setConfirmation(undefined);}}
        onBackdropClick={() => {if (!busy) setConfirmation(undefined);}}>
        <div className="multiTeamMatchConfirmation__message">
          {newMatches > 0 && <p>{newMatches === 1 ? '1 finished match will be created.' : `${newMatches} finished matches will be created.`}</p>}
          <p>{confirmation === 'FINISH' ? 'The match entries will be saved and the competition marked Finished.' : 'Your match entries will be saved.'}</p>
          {confirmation === 'FINISH' && <p className="multiTeamMatchConfirmation__hint">Overall points or places are optional.</p>}
        </div>
      </SimpleModal>
    </>}
  </section>;
};
